// apps/client/src/pages/auth/CustomPasswordResetHandler.tsx

import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import AuthLayout from '../../components/layout/AuthLayout';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';

// Password validation utilities
const validatePasswordRequirements = (password: string) => {
  const requirements = {
    length: {
      met: password.length >= 12,
      message: 'At least 12 characters'
    },
    uppercase: {
      met: /[A-Z]/.test(password),
      message: 'One uppercase letter'
    },
    lowercase: {
      met: /[a-z]/.test(password),
      message: 'One lowercase letter'
    },
    number: {
      met: /\d/.test(password),
      message: 'One number'
    },
    special: {
      met: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
      message: 'One special character'
    }
  };

  const allMet = Object.values(requirements).every(req => req.met);

  return {
    requirements,
    allMet,
    score: Object.values(requirements).filter(req => req.met).length
  };
};

const calculatePasswordStrength = (password: string) => {
  if (!password) return { label: '', percentage: 0, color: '' };

  const validation = validatePasswordRequirements(password);
  const percentage = (validation.score / 5) * 100;

  if (percentage === 100) return { label: 'Excellent', percentage, color: 'from-green-500 to-emerald-600' };
  if (percentage >= 80) return { label: 'Strong', percentage, color: 'from-green-400 to-green-500' };
  if (percentage >= 60) return { label: 'Good', percentage, color: 'from-yellow-500 to-green-400' };
  if (percentage >= 40) return { label: 'Fair', percentage, color: 'from-orange-500 to-yellow-500' };
  return { label: 'Weak', percentage, color: 'from-red-500 to-red-600' };
};

// VALUTTEN Logo component
const ValuttenLogo = () => (
  <img
    src="/valutten-logo.jpg"
    alt="VALUTTEN"
    className="h-12 w-auto object-contain"
  />
);

const CustomPasswordResetHandler = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState<string | null>(null);
  const [status, setStatus] = useState<'verifying' | 'ready' | 'invalid' | 'success' | 'submitting'>('verifying');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [passwordValidation, setPasswordValidation] = useState<any>(null);
  const [passwordStrength, setPasswordStrength] = useState<any>(null);

  // Validate token on mount
  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (!tokenParam) {
      setError('Invalid or missing reset link. Please request a new one.');
      setStatus('invalid');
      return;
    }

    setToken(tokenParam);

    // Call validateResetToken Cloud Function
    const validateResetToken = httpsCallable(functions, 'validateResetToken');
    validateResetToken({ token: tokenParam })
      .then((result: any) => {
        if (result.data.valid) {
          setStatus('ready');
        } else {
          setError(result.data.message || 'The reset link is invalid or has expired. Please request a new one.');
          setStatus('invalid');
        }
      })
      .catch((err: any) => {
        console.error('Token validation error:', err);
        setError('Unable to verify reset link. Please try again or request a new one.');
        setStatus('invalid');
      });
  }, [searchParams]);

  // Update password validation as user types
  useEffect(() => {
    if (newPassword) {
      const validation = validatePasswordRequirements(newPassword);
      const strength = calculatePasswordStrength(newPassword);
      setPasswordValidation(validation);
      setPasswordStrength(strength);
    } else {
      setPasswordValidation(null);
      setPasswordStrength(null);
    }
  }, [newPassword]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate password requirements
    if (!passwordValidation?.allMet) {
      setError('Password does not meet security requirements.');
      return;
    }

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (!token) return;

    setStatus('submitting');

    try {
      // Call resetPasswordWithToken Cloud Function
      const resetPasswordWithToken = httpsCallable(functions, 'resetPasswordWithToken');
      const result = await resetPasswordWithToken({
        token,
        newPassword
      });

      if ((result.data as any).success) {
        setStatus('success');
        // Auto-redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login', { state: { message: 'Password reset successfully. Please log in with your new password.' } });
        }, 3000);
      } else {
        setError((result.data as any).message || 'Failed to reset password. Please try again.');
        setStatus('ready');
      }
    } catch (err: any) {
      console.error('Password reset error:', err);
      const errorMessage = err.message || 'Failed to reset password. Please try again or request a new link.';
      setError(errorMessage);
      setStatus('ready');
    }
  };

  const handleRequestNewLink = () => {
    navigate('/password-reset');
  };

  return (
    <AuthLayout>
      <div className="w-full max-w-lg p-8 rounded-xl bg-slate-900/80 border border-slate-800">
        {/* VALUTTEN Header */}
        <div className="flex flex-col items-center justify-center mb-6">
          <ValuttenLogo />
          <div className="mt-4 w-14 h-14 rounded-full bg-emerald-500/20 flex items-center justify-center">
            <Lock className="w-7 h-7 text-emerald-500" />
          </div>
        </div>
        <h1 className="text-2xl font-bold text-center mb-2 text-white">Reset Password</h1>

        {/* Verifying State */}
        {status === 'verifying' && (
          <div className="text-center py-8">
            <div className="inline-block w-8 h-8 border-4 border-slate-600 border-t-emerald-500 rounded-full animate-spin"></div>
            <p className="mt-4 text-slate-400">Verifying reset link...</p>
          </div>
        )}

        {/* Invalid State */}
        {status === 'invalid' && (
          <div>
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <h2 className="text-lg font-semibold text-red-400 mb-1">Invalid Reset Link</h2>
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            </div>
            <button
              onClick={handleRequestNewLink}
              className="w-full py-3 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:ring-offset-slate-900"
            >
              Request New Reset Link
            </button>
            <p className="text-center mt-4 text-slate-400 text-sm">
              <Link to="/login" className="text-emerald-400 hover:text-emerald-300 font-medium">
                Back to Login
              </Link>
            </p>
          </div>
        )}

        {/* Success State */}
        {status === 'success' && (
          <div className="text-center">
            <div className="mb-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
              <div className="text-left">
                <h2 className="text-lg font-semibold text-green-400 mb-1">Success!</h2>
                <p className="text-green-400 text-sm">Your password has been reset successfully.</p>
              </div>
            </div>
            <p className="mb-4 text-slate-300">Redirecting to login page...</p>
            <div className="flex flex-col space-y-3 mt-4">
              <Link
                to="/login"
                className="block text-center w-full bg-emerald-600 text-white p-3 rounded-lg hover:bg-emerald-700 font-semibold transition-colors"
              >
                Go to Login
              </Link>
            </div>
          </div>
        )}

        {/* Ready State - Reset Password Form */}
        {(status === 'ready' || status === 'submitting') && (
          <>
            <p className="text-center text-slate-400 text-sm mb-6">
              Enter your new password below
            </p>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* New Password Field */}
              <div>
                <label htmlFor="new-password" className="block text-sm font-medium text-slate-300 mb-2">
                  New password
                </label>
                <div className="relative">
                  <input
                    id="new-password"
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter your new password"
                    className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent pr-12"
                    required
                    disabled={status === 'submitting'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
                    disabled={status === 'submitting'}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Confirm Password Field */}
              <div>
                <label htmlFor="confirm-password" className="block text-sm font-medium text-slate-300 mb-2">
                  Confirm password
                </label>
                <div className="relative">
                  <input
                    id="confirm-password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your new password"
                    className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent pr-12"
                    required
                    disabled={status === 'submitting'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
                    disabled={status === 'submitting'}
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Password Strength Indicator */}
              {newPassword && passwordStrength && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-400">Password Strength</span>
                    <span className={`font-semibold ${
                      passwordStrength.label === 'Excellent' ? 'text-green-400' :
                      passwordStrength.label === 'Strong' ? 'text-green-500' :
                      passwordStrength.label === 'Good' ? 'text-yellow-400' :
                      passwordStrength.label === 'Fair' ? 'text-orange-400' :
                      'text-red-400'
                    }`}>
                      {passwordStrength.label}
                    </span>
                  </div>
                  <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden">
                    <div
                      className={`h-full bg-gradient-to-r ${passwordStrength.color} transition-all duration-300`}
                      style={{ width: `${passwordStrength.percentage}%` }}
                    />
                  </div>

                  {/* Password Requirements Checklist */}
                  <div className="mt-4 space-y-2">
                    {passwordValidation && Object.entries(passwordValidation.requirements).map(([key, req]: [string, any]) => (
                      <div key={key} className="flex items-center gap-2 text-sm">
                        {req.met ? (
                          <CheckCircle className="w-4 h-4 text-green-400" />
                        ) : (
                          <div className="w-4 h-4 rounded-full border-2 border-slate-600" />
                        )}
                        <span className={req.met ? 'text-green-400' : 'text-slate-400'}>
                          {req.message}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Password Match Indicator */}
              {confirmPassword && (
                <div className="flex items-center gap-2 text-sm">
                  {newPassword === confirmPassword ? (
                    <>
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span className="text-green-400">Passwords match</span>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-4 h-4 text-red-400" />
                      <span className="text-red-400">Passwords do not match</span>
                    </>
                  )}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={!passwordValidation?.allMet || newPassword !== confirmPassword || status === 'submitting'}
                className="w-full py-3 px-4 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-700 disabled:text-slate-500 text-white font-semibold rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:ring-offset-slate-900"
              >
                {status === 'submitting' ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Resetting Password...
                  </span>
                ) : (
                  'Reset Password'
                )}
              </button>
            </form>

            <p className="text-center mt-4 text-slate-400 text-sm">
              <Link to="/login" className="text-emerald-400 hover:text-emerald-300 font-medium">
                Back to Login
              </Link>
            </p>
          </>
        )}
      </div>
    </AuthLayout>
  );
};

export default CustomPasswordResetHandler;
